import React, { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { CalendarDaysIcon, CalendarRangeIcon, CheckIcon, FilterIcon, CalendarIcon, ClockIcon, StarIcon, ChevronDown } from "lucide-react";
import moment from "moment";
import { getDateRange } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface SelectDateRangeProps {
  onDateRangeChange: (fromDate: Date, toDate: Date) => void;
  initialFromDate?: Date;
  initialToDate?: Date;
  initialRange?: string;
}

export const SelectDateRange = ({
  onDateRangeChange,
  initialFromDate,
  initialToDate,
  initialRange = "14",
}: SelectDateRangeProps) => {
  const [selectedDateRange, setSelectedDateRange] = useState(initialRange);
  const [fromDate, setFromDate] = useState<Date | null>(
    initialFromDate || null
  );

  const [toDate, setToDate] = useState<Date | null>(initialToDate || null);
  const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState(false);
  const [errors, setErrors] = useState<{ fromDate?: string; toDate?: string }>(
    {}
  );
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    if (selectedDateRange !== "custom") {
      const { from, to } = getDateRange(selectedDateRange);
      setFromDate(from);
      setToDate(to);
      if (from && to) {
        onDateRangeChange(from, to);
      }
    }
  }, [selectedDateRange]);

  const handleDateRangeSelect = (value: string) => {
    setSelectedDateRange(value);
    setPopoverOpen(false);
  };

  const validate = () => {
    const newErrors: { fromDate?: string; toDate?: string } = {};

    if (!fromDate && toDate) {
      newErrors.fromDate = "Please select a start date";
    }
    if (!toDate && fromDate) {
      newErrors.toDate = "Please select an end date";
    }
    if (fromDate && toDate && moment(toDate).isBefore(moment(fromDate))) {
      newErrors.toDate = "End date must be after start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApplyCustomDates = () => {
    if (!validate()) return;
    if (!fromDate || !toDate) return;

    let finalFromDate = fromDate;
    let finalToDate = toDate;

    if (moment(fromDate).isSame(toDate, 'day')) {
      finalFromDate = moment(fromDate).startOf('day').toDate();
      finalToDate = moment(toDate).endOf('day').toDate();
    }

    onDateRangeChange(finalFromDate, finalToDate);
    setSelectedDateRange("custom");
    setDateRangeDialogOpen(false);
  };

  const renderDateRangeButton = () => {
    if (selectedDateRange === "custom") {
      return (
        <div className="w-full flex justify-between pr-4">
          <span>{moment(fromDate).format("MMM D, YYYY")}</span>
          <span>-</span>
          <span>{moment(toDate).format("MMM D, YYYY")}</span>
        </div>
      );
    }

    const ranges: Record<string, string> = {
      "7": "Last 7 days",
      "14": "Last 14 days", 
      "30": "Last 30 days",
      "60": "Last 60 days",
      "90": "Last 90 days",
      "180": "Last 180 days",
      "365": "Last 365 days",
      "calendar_month": "This Month",
      "calendar_quarter": "This Quarter", 
      "calendar_year": "This Year",
      "calendar_week": "This Week",
      "Q1": "Q1",
      "Q2": "Q2",
      "Q3": "Q3", 
      "Q4": "Q4",
      "January": "January",
      "February": "February",
      "March": "March",
      "April": "April",
      "May": "May",
      "June": "June",
      "July": "July",
      "August": "August",
      "September": "September",
      "October": "October",
      "November": "November",
      "December": "December",
      "2025": "2025",
      "2024": "2024",
      "2023": "2023",
      "2022": "2022",
      "2025_Q1": "2025 Q1",
      "2025_Q2": "2025 Q2",
      "2025_Q3": "2025 Q3", 
      "2025_Q4": "2025 Q4",
      "2024_Q1": "2024 Q1",
      "2024_Q2": "2024 Q2",
      "2024_Q3": "2024 Q3",
      "2024_Q4": "2024 Q4",
      "2023_Q1": "2023 Q1",
      "2023_Q2": "2023 Q2",
      "2023_Q3": "2023 Q3",
      "2023_Q4": "2023 Q4"
    };

    return ranges[selectedDateRange] || "Select date range";
  };

  return (
    <div>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-[250px] justify-between">
            <div className="flex items-center">
              <CalendarIcon className="w-4 h-4 mr-2" />
              {renderDateRangeButton()}
            </div>
            <ChevronDown className="w-4 h-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent side="right" align="center" className="w-[550px] max-h-[85vh] overflow-y-auto p-0">
          <div>
            <div className="grid grid-cols-4 border-b">
              <h4 className="border-r pb-2 p-2 font-medium flex items-center justify-center text-sm">
                <ClockIcon className="w-3 h-3 mr-2" />
                Quick
              </h4>
              <h4 className="border-r pb-2 p-2 font-medium flex items-center justify-center text-sm">
                <StarIcon className="w-3 h-3 mr-2" />
                Favourites
              </h4>
              <h4 className="border-r pb-2 p-2 font-medium flex items-center justify-center text-sm">
                <CalendarDaysIcon className="w-3 h-3 mr-2" />
                Months
              </h4>
              <h4 className="pb-2 p-2 font-medium flex items-center justify-center text-sm">
                <CalendarIcon className="w-3 h-3 mr-2" />
                Quarters
              </h4>
            </div>

            <div className="grid grid-cols-4">
              <div className="space-y-2 border-r p-2 opacity-70 hover:opacity-100">
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("7")}>Last 7 days</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("14")}>Last 14 days</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("30")}>Last 30 days</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("60")}>Last 60 days</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("90")}>Last 90 days</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("180")}>Last 180 days</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("365")}>Last 365 days</Button>
              </div>

              <div className="space-y-2 border-r p-2 opacity-70 hover:opacity-100">
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("calendar_week")}>This Week</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("calendar_month")}>This Month</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("calendar_quarter")}>This Quarter</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("calendar_year")}>This Year</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2025")}>2025</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2024")}>2024</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2023")}>2023</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2022")}>2022</Button>
              </div>

              <div className="space-y-2 border-r p-2 opacity-70 hover:opacity-100">
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("January")}>January</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("February")}>February</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("March")}>March</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("April")}>April</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("May")}>May</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("June")}>June</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("July")}>July</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("August")}>August</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("September")}>September</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("October")}>October</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("November")}>November</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("December")}>December</Button>
              </div>

              <div className="space-y-2 p-2 opacity-70 hover:opacity-100">
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2025_Q4")}>2025 Q4</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2025_Q3")}>2025 Q3</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2025_Q2")}>2025 Q2</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2025_Q1")}>2025 Q1</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2024_Q4")}>2024 Q4</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2024_Q3")}>2024 Q3</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2024_Q2")}>2024 Q2</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2024_Q1")}>2024 Q1</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2023_Q4")}>2023 Q4</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2023_Q3")}>2023 Q3</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2023_Q2")}>2023 Q2</Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleDateRangeSelect("2023_Q1")}>2023 Q1</Button>
              </div>
            </div>

            <div className="border-t p-4">
              <Button variant="secondary" className="w-full" onClick={() => {
                setPopoverOpen(false);
                setDateRangeDialogOpen(true);
              }}>
                <CalendarRangeIcon className="w-4 h-4 mr-2" />
                Custom Range
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={dateRangeDialogOpen} onOpenChange={setDateRangeDialogOpen}>
        <DialogContent className="w-[625px]">
          <DialogHeader>
            <DialogTitle>Select Custom Date Range</DialogTitle>
          </DialogHeader>
          <div className="w-full flex flex-col gap-10">
            <div>
              <div className="flex gap-4">
                <div className="w-full lg:w-1/2">
                  <Label>From</Label>
                  <DatePicker
                    date={fromDate ?? undefined}
                    onDateChange={(date) => setFromDate(date ?? null)}
                    className={errors.fromDate ? "border border-red-500" : ""}
                  />
                  {errors.fromDate && (
                    <div className="text-red-500 text-sm mt-1">
                      {errors.fromDate}
                    </div>
                  )}
                </div>
                <div className="w-full lg:w-1/2">
                  <Label>To</Label>
                  <DatePicker
                    date={toDate ?? undefined}
                    onDateChange={(date) => setToDate(date ?? null)}
                    className={errors.toDate ? "border border-red-500" : ""}
                  />
                  {errors.toDate && (
                    <div className="text-red-500 text-sm mt-1">
                      {errors.toDate}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              className="mt-4 w-full bg-blue-600 hover:bg-blue-800"
              type="submit"
              onClick={handleApplyCustomDates}
            >
              <FilterIcon className="w-4 h-4 mr-2" />
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
